import { Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { createFeatureCollection } from '../../../map/utils/createFeatureCollection';
import {
  MapDrawIDEnum,
  clearMapDrawFeatures,
  selectCurrMapDrawFeatures,
  selectUploadedBlockGeoJSON,
  setUploadedBlockGeoJSON,
} from '../../onboardingSlice';
import { isValidGeoFeature } from '../../onboardingUtils';
import SubmitSnackbar from '../components/SubmitSnackBar';
import { useGetCurrOnboardBlock } from '../hooks/useGetCurrOnboardBlock';
import BlockBoundaryFormTabDrawSection from './BlockBoundaryFormTabDrawSection';
import BlockBoundaryFormTabUploadSection from './BlockBoundaryFormTabUploadSection';
import useSubmitBlockMapItem from './hooks/useSubmitBlockMapItem';

const BlockBoundaryFormTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currMapDrawFeatures = useAppSelector(selectCurrMapDrawFeatures);
  const uploadedBlockGeoJSON = useAppSelector(selectUploadedBlockGeoJSON);

  const {
    blockSubmitHandler,
    snackbarOpen,
    setSnackbarOpen,
    errorMessage,
    setErrorMessage,
  } = useSubmitBlockMapItem();

  const { currentData: block } = useGetCurrOnboardBlock();

  // Reset the status when the block changes
  useEffect(() => {
    setErrorMessage('');
    setSnackbarOpen(false);
  }, [block]);

  const handleSendBlockBoundaryClick = async (type: 'draw' | 'upload') => {
    setErrorMessage('');
    setSnackbarOpen(false);

    if (!block) {
      setErrorMessage('No block found');
      setSnackbarOpen(true);
      return;
    }

    // Check if the feature is valid in draw mode, upload file mode is always valid bc it's checked before being put in redux
    const isValid =
      type === 'draw'
        ? isValidGeoFeature(
            currMapDrawFeatures,
            'Polygon',
            MapDrawIDEnum.blockBoundary
          )
        : true;

    const newBlockFeatureCollection =
      type === 'draw'
        ? createFeatureCollection([
            currMapDrawFeatures[MapDrawIDEnum.blockBoundary],
          ])
        : uploadedBlockGeoJSON;

    if (!isValid || !newBlockFeatureCollection) {
      setErrorMessage('Invalid block boundary.');
      setSnackbarOpen(true);
      return;
    }

    try {
      await blockSubmitHandler({
        ...block,
        geoPolygon: newBlockFeatureCollection,
      });

      dispatch(clearMapDrawFeatures());
      dispatch(setUploadedBlockGeoJSON(null));
    } catch (error) {
      console.log('Error: ', error);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateRows: '1fr',
        gridTemplateColumns: '1fr min-content 1fr',
        gap: '1.5rem',
      }}
    >
      <SubmitSnackbar snackbarOpen={snackbarOpen} errorMessage={errorMessage} />
      <BlockBoundaryFormTabDrawSection
        handleSendBlockBoundaryClick={handleSendBlockBoundaryClick}
        currMapDrawFeatures={currMapDrawFeatures}
      />
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography variant='h5'>{t('OR', { ns: 'common' })}</Typography>
      </Box>
      <BlockBoundaryFormTabUploadSection
        handleSendBlockBoundaryClick={handleSendBlockBoundaryClick}
        isSubmitDisabled={!uploadedBlockGeoJSON}
      />
    </Box>
  );
};

export default BlockBoundaryFormTab;
